import React, { useEffect, useState } from "react";
import {
  Box, Select, MenuItem, Button, TextField, TextareaAutosize, FormControl, Table,
  TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow,
  Paper, TablePagination
} from "@mui/material";
import PageTitle from "../../common/PageTitle";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { getAPI, postAPI } from "../../network";
import Loader from "../../common/Loader";
import { tableHeader, tableData, validation } from "../../utils";

export default function RiskAssessment() {
  const navigate = useNavigate();
  const { ids } = useParams();
  const [loader, setLoader] = useState(false);
  const [assessment, setAssessment] = useState([]);
  const [siteId, setSiteId] = useState('');
  const [siteOptions, setSiteOptions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [assessName, setAssessName] = useState('');
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(5);
  const toastObj = { position: toast.POSITION.TOP_RIGHT };
  const companyId = localStorage.getItem('companyIdLS') || undefined;

  useEffect(() => {
    fetchSites();
    fetchQuestions();
  }, []);

  const fetchSites = async () => {
    const result = await getAPI(`/sites?companyId=${companyId}`);
    if (result) {
      const formatted = result.map(site => ({
        label: site.name, value: site._id
      }));
      setSiteOptions(formatted);
    }
  };

  const fetchQuestions = async () => {
    setLoader(true);
    const result = await getAPI(`/risk-assessment/category-question/by-category/${ids}?companyId=${companyId}`);
    if (result?.length) {
      setAssessment(result);
      setAssessName(result[0].riskAssessmentCategoryId.name);
    }
    setLoader(false);
  };

  const updateAnswer = (questionId, field, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        questionId,
        [field]: value
      }
    }));
  };

  const handleFileChange = (questionId, file) => {
    updateAnswer(questionId, 'media', file);
  };

  const validateAnswer = (ans) => {
    const requiredFields = ['cat', 'subCat', 'inherit', 'like', 'impact', 'rating'];
    return requiredFields.every(field => !!ans?.[field]);
  };

  const submitForm = async () => {
    if (!siteId) return toast.error('Please select a site', toastObj);

    const questionAnswers = Object.values(answers);

    if (!questionAnswers.length || questionAnswers.some(ans => !validateAnswer(ans))) {
      return toast.error('Please complete all required fields', toastObj);
    }

    const payload = {
      riskAssessmentCategoryId: ids,
      siteId,
      questions: questionAnswers
    };

    setLoader(true);
    const res = await postAPI('/risk-assessment-report', payload);
    setLoader(false);

    if (res) {
      toast.success("Form submitted successfully!", toastObj);
      navigate('/riskAssessment/forms');
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ height: "inherit" }}>
      <Loader loader={loader} />
      <PageTitle title="Risk Assessment Forms" subTitle={assessName} />

      {/* Site Select */}
      <Box align="left" sx={tableData}>
        <FormControl sx={{ m: 1, width: "40%" }}>
          <b>Site</b>
          <Select
            value={siteId || ''}
            onChange={(e) => setSiteId(e.target.value)}
            displayEmpty
            inputProps={{ 'aria-label': 'Select Site' }}
            sx={{
              borderRadius: 2,
              boxShadow: 1,
              borderColor: "#707070"
            }}
          >
            {siteOptions.map((item, index) => (
              <MenuItem key={index} value={item.value}>{item.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Table of Questions */}
      <Box display="flex" sx={{ my: "4rem" }}>
        <TableContainer component={Paper} sx={{ mx: "0.8rem" }}>
          <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow className="table-header">
                <TableCell align="left">Sr No.</TableCell>
                <TableCell align="left" sx={tableHeader}>Question</TableCell>
                <TableCell align="left" sx={tableHeader}>Category</TableCell>
                <TableCell align="left" sx={tableHeader}>Sub-Category</TableCell>
                <TableCell align="left" sx={tableHeader}>Inherent Risk</TableCell>
                <TableCell align="left" sx={tableHeader}>Likelihood</TableCell>
                <TableCell align="left" sx={tableHeader}>Impact</TableCell>
                <TableCell align="left" sx={tableHeader}>Rating</TableCell>
                <TableCell align="left" sx={tableHeader}>Notes</TableCell>
                <TableCell align="left" sx={tableHeader}>Media</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {assessment.slice(page * perPage, page * perPage + perPage).map((item, index) => {
                const current = answers[item.id] || {};
                return (
                  <TableRow key={item.id}>
                    <TableCell>{page * perPage + index + 1}</TableCell>
                    <TableCell>{item.question}</TableCell>

                    {/* Category */}
                    <TableCell>
                      <FormControl fullWidth>
                        <Select
                          value={current.cat || ''}
                          onChange={(e) => updateAnswer(item.id, 'cat', e.target.value)}
                        >
                          {item.category.map(opt => (
                            <MenuItem key={opt._id} value={opt._id}>{opt.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>

                    {/* SubCategory */}
                    <TableCell>
                      <FormControl fullWidth>
                        <Select
                          value={current.subCat || ''}
                          onChange={(e) => updateAnswer(item.id, 'subCat', e.target.value)}
                        >
                          {item.subCategory.map(opt => (
                            <MenuItem key={opt._id} value={opt._id}>{opt.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>

                    {/* Inherent Risk */}
                    <TableCell>
                      <FormControl fullWidth>
                        <Select
                          value={current.inherit || ''}
                          onChange={(e) => updateAnswer(item.id, 'inherit', e.target.value)}
                        >
                          {item.riskIdentified.map(opt => (
                            <MenuItem key={opt._id} value={opt._id}>{opt.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>

                    {/* Likelihood */}
                    <TableCell>
                      <FormControl fullWidth>
                        <Select
                          value={current.like || ''}
                          onChange={(e) => updateAnswer(item.id, 'like', e.target.value)}
                        >
                          {item.likelihood.map(opt => (
                            <MenuItem key={opt._id} value={opt._id}>{opt.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>

                    {/* Impact */}
                    <TableCell>
                      <FormControl fullWidth>
                        <Select
                          value={current.impact || ''}
                          onChange={(e) => updateAnswer(item.id, 'impact', e.target.value)}
                        >
                          {item.impact.map(opt => (
                            <MenuItem key={opt._id} value={opt._id}>{opt.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>

                    {/* Rating */}
                    <TableCell>
                      <FormControl fullWidth>
                        <Select
                          value={current.rating || ''}
                          onChange={(e) => updateAnswer(item.id, 'rating', e.target.value)}
                        >
                          {item.rating.map(opt => (
                            <MenuItem key={opt._id} value={opt._id}>{opt.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>

                    {/* Notes */}
                    <TableCell>
                      <TextareaAutosize
                        minRows={3}
                        value={current.notes || ''}
                        onChange={(e) => updateAnswer(item.id, 'notes', e.target.value)}
                        placeholder="Enter notes"
                        style={{ width: "100%", fontSize: 12, padding: 5 }}
                      />
                    </TableCell>

                    {/* File Upload */}
                    <TableCell>
                      <TextField
                        type="file"
                        onChange={(e) => handleFileChange(item.id, e.target.files[0])}
                        inputProps={{ accept: "image/*,application/pdf" }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>

            <TableFooter>
              <TableRow>
                <TableCell colSpan={10}>
                  <Button
                    onClick={submitForm}
                    variant="contained"
                    sx={{ float: "right", mt: 2, backgroundColor: "#42505C", color: "white" }}
                  >
                    Submit Form
                  </Button>
                </TableCell>
              </TableRow>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  count={assessment.length}
                  rowsPerPage={perPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  colSpan={10}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
